import { useQuery } from '@apollo/client';

import InviteRostersByProgramIdGql from 'bundles/enterprise-admin-learners/queries/InviteRostersByProgramId.graphql';
import type {
  InviteRostersByProgramIdQuery,
  InviteRostersByProgramIdQueryVariables,
} from 'bundles/enterprise-admin-learners/queries/__generated__/InviteRostersByProgramId';

/**
 * Resolves the roster that a program/group has under a specific contract, so the
 * invite mutation can be scoped to it (see ContractSelectionStep). Skipped entirely
 * for single-contract orgs (no `contractId`) — the mutation works without a
 * `rosterId` today, matching pre-ENTADMIN-1962 behavior.
 */
export const useRosterIdForContract = (programId: string | undefined, contractId: string | undefined) => {
  const { data, loading, error } = useQuery<InviteRostersByProgramIdQuery, InviteRostersByProgramIdQueryVariables>(
    InviteRostersByProgramIdGql,
    {
      variables: { programIds: programId ? [programId] : [] },
      skip: !programId || !contractId,
      context: { clientName: 'gatewayGql' },
    }
  );

  const rosters = data?.EnterpriseRosters?.queryRostersByProgramIdsOrContractIdsOrBothAndIncludeDeleted?.elements;
  const rosterId = rosters?.find((roster) => roster.contract?.id === contractId)?.id;
  // A query error leaves `rosters` undefined, same as a not-yet-loaded state — treat it
  // as "no match" rather than falling through and submitting an unscoped invite despite
  // a contract having been selected.
  const hasNoMatch =
    !loading && Boolean(programId) && Boolean(contractId) && (Boolean(error) || (rosters !== undefined && !rosterId));

  return { rosterId, loading, hasNoMatch };
};

export default useRosterIdForContract;
